import axios from "axios";
import { API_URL } from "../config/constants";

// Crear instancia de axios con la URL base desde la configuración
const api = axios.create({
  baseURL: API_URL, // Usar la URL de la configuración
  headers: {
    "Content-Type": "application/json",
  },
});

// Interceptor: enviar JWT en todas las peticiones
api.interceptors.request.use((config) => {
  const token = localStorage.getItem("token");
  if (token) {
    config.headers.Authorization = `Bearer ${token}`;
  }
  return config;
});

// Interceptor: manejar errores 401 (token inválido/expirado)
api.interceptors.response.use(
  (response) => response,
  (error) => {
    if (error.response?.status === 401) {
      // Solo redirigir si NO es una petición de login (evitar loop infinito)
      const isLoginRequest = error.config?.url?.includes("/api/auth/login");
      if (!isLoginRequest) {
        localStorage.removeItem("token");
        localStorage.removeItem("user");
        window.location.href = "/";
      }
    }
    return Promise.reject(error);
  }
);

// Servicio de autenticación
export const authService = {
  // Login con usuario y contraseña → JWT
  login: async (username, password) => {
    try {
      const response = await api.post("/api/auth/login", { username, password });
      return response.data;
    } catch (error) {
      throw (
        error.response?.data || {
          success: false,
          message: "Error de conexión",
        }
      );
    }
  },

  // Obtener datos del usuario autenticado (vía JWT)
  getMe: async () => {
    try {
      const response = await api.get("/api/auth/me");
      return response.data;
    } catch (error) {
      throw (
        error.response?.data || {
          success: false,
          message: "Error de conexión",
        }
      );
    }
  },
};

// Servicio de gestión de usuarios (solo admin)
export const userService = {
  // Listar todos los usuarios
  getUsers: async () => {
    try {
      const response = await api.get("/api/users");
      return response.data;
    } catch (error) {
      throw (
        error.response?.data || {
          success: false,
          message: "Error obteniendo usuarios",
        }
      );
    }
  },

  // Crear un nuevo usuario
  createUser: async (userData) => {
    try {
      const response = await api.post("/api/users", userData);
      return response.data;
    } catch (error) {
      throw (
        error.response?.data || {
          success: false,
          message: "Error creando usuario",
        }
      );
    }
  },

  // Actualizar un usuario existente
  updateUser: async (userId, userData) => {
    try {
      const response = await api.put(`/api/users/${userId}`, userData);
      return response.data;
    } catch (error) {
      throw (
        error.response?.data || {
          success: false,
          message: "Error actualizando usuario",
        }
      );
    }
  },

  // Cambiar contraseña de un usuario
  changePassword: async (userId, newPassword) => {
    try {
      const response = await api.put(`/api/users/${userId}/password`, { newPassword });
      return response.data;
    } catch (error) {
      throw (
        error.response?.data || {
          success: false,
          message: "Error cambiando contraseña",
        }
      );
    }
  },

  // Eliminar un usuario
  deleteUser: async (userId) => {
    try {
      const response = await api.delete(`/api/users/${userId}`);
      return response.data;
    } catch (error) {
      throw (
        error.response?.data || {
          success: false,
          message: "Error eliminando usuario",
        }
      );
    }
  },

  // Obtener logs de actividad de un usuario
  getUserLogs: async (userId, { limit = 100, offset = 0 } = {}) => {
    try {
      const response = await api.get(`/api/audit-logs/user/${userId}`, {
        params: { limit, offset },
      });
      return response.data;
    } catch (error) {
      throw (
        error.response?.data || {
          success: false,
          message: "Error obteniendo logs de actividad",
        }
      );
    }
  },
};

// Servicio de videos
export const videoService = {
  // Subir video (ahora acepta una función de callback para el progreso)
  uploadVideo: async (formData, onUploadProgressCallback) => {
    try {
      const response = await api.post("/api/videos/upload", formData, {
        headers: {
          "Content-Type": "multipart/form-data",
        },
        // Pasar la función de callback directamente a la configuración de Axios
        onUploadProgress: onUploadProgressCallback,
      });
      return response.data;
    } catch (error) {
      throw (
        error.response?.data || {
          success: false,
          message: "Error al subir el video",
        }
      );
    }
  },

  // Obtener videos
  getVideos: async () => {
    try {
      const response = await api.get("/api/videos");
      return response.data;
    } catch (error) {
      throw (
        error.response?.data || {
          success: false,
          message: "Error al obtener los videos",
        }
      );
    }
  },

  // Renombrar video (cambiar marca)
  renameVideo: async (fileId, newMarca) => {
    try {
      // Usamos PATCH porque es una actualización parcial (solo el nombre basado en la marca)
      const response = await api.patch(`/api/videos/${fileId}`, { newMarca });
      return response.data; // Devuelve { success: true, message: "...", newName: "..." }
    } catch (error) {
      throw (
        error.response?.data || {
          success: false,
          message: "Error de conexión al renombrar el archivo.",
        }
      );
    }
  },

  // Eliminar video
  deleteVideo: async (fileId) => {
    try {
      const response = await api.delete(`/api/videos/${fileId}`);
      return response.data; // Devuelve { success: true, message: "..." }
    } catch (error) {
      throw (
        error.response?.data || {
          success: false,
          message: "Error de conexión al eliminar el archivo.",
        }
      );
    }
  },

  // Enviar video a Vimeo
  sendToVimeo: async (fileId, title, description, privacy) => {
    try {
      const response = await api.post(`/api/vimeo/upload/${fileId}`, {
        title,
        description,
        privacy
      });
      return response.data;
    } catch (error) {
      throw (
        error.response?.data || {
          success: false,
          message: "Error al enviar el video a Vimeo",
        }
      );
    }
  },

  // Eliminar video de Vimeo
  deleteFromVimeo: async (fileId) => {
    try {
      const response = await api.delete(`/api/vimeo/${fileId}`);
      return response.data;
    } catch (error) {
      throw (
        error.response?.data || {
          success: false,
          message: "Error al eliminar el video de Vimeo",
        }
      );
    }
  },

  // Reconciliar metadata de Vimeo para un archivo de Drive
  syncVimeoStatus: async (fileId) => {
    try {
      const response = await api.get(`/api/vimeo/sync/${fileId}`);
      return response.data;
    } catch (error) {
      throw (
        error.response?.data || {
          success: false,
          message: "Error sincronizando estado de Vimeo",
        }
      );
    }
  },

  // Intercambiar código de Vimeo por token
  exchangeVimeoCode: async (code, redirectUri) => {
    try {
      const response = await api.post('/api/vimeo/exchange-code', {
        code,
        redirectUri
      });
      return response.data;
    } catch (error) {
      throw (
        error.response?.data || {
          success: false,
          message: "Error intercambiando código de Vimeo",
        }
      );
    }
  },

  // Obtener configuración de Vimeo
  getVimeoConfig: async () => {
    try {
      const response = await api.get('/api/vimeo/config');
      return response.data;
    } catch (error) {
      throw (
        error.response?.data || {
          success: false,
          message: "Error obteniendo configuración de Vimeo",
        }
      );
    }
  },

  // Actualizar configuración de Vimeo
  updateVimeoConfig: async (config) => {
    try {
      const response = await api.post('/api/vimeo/config', config);
      return response.data;
    } catch (error) {
      throw (
        error.response?.data || {
          success: false,
          message: "Error actualizando configuración de Vimeo",
        }
      );
    }
  },

  // Limpiar autenticación de Vimeo
  clearVimeoAuth: async () => {
    try {
      const response = await api.delete('/api/vimeo/auth');
      return response.data;
    } catch (error) {
      throw (
        error.response?.data || {
          success: false,
          message: "Error limpiando autenticación de Vimeo",
        }
      );
    }
  },

  // Obtener cuentas de Vimeo conectadas
  getVimeoAccounts: async () => {
    try {
      const response = await api.get('/api/vimeo/accounts');
      return response.data;
    } catch (error) {
      throw (
        error.response?.data || {
          success: false,
          message: "Error obteniendo cuentas de Vimeo",
        }
      );
    }
  },

  // Agregar una cuenta de Vimeo adicional
  addVimeoAccount: async (accessToken, userInfo) => {
    try {
      const response = await api.post('/api/vimeo/accounts', {
        accessToken,
        userInfo
      });
      return response.data;
    } catch (error) {
      throw (
        error.response?.data || {
          success: false,
          message: "Error agregando cuenta de Vimeo",
        }
      );
    }
  },

  // Eliminar una cuenta de Vimeo adicional
  removeVimeoAccount: async (accountId) => {
    try {
      const response = await api.delete(`/api/vimeo/accounts/${accountId}`);
      return response.data;
    } catch (error) {
      throw (
        error.response?.data || {
          success: false,
          message: "Error eliminando cuenta de Vimeo",
        }
      );
    }
  },

  // Seleccionar la cuenta activa para subir videos
  selectVimeoAccount: async (accountId) => {
    try {
      const response = await api.post('/api/vimeo/accounts/select', {
        accountId
      });
      return response.data;
    } catch (error) {
      throw (
        error.response?.data || {
          success: false,
          message: "Error seleccionando cuenta de Vimeo",
        }
      );
    }
  },

  // Obtener estado de API Key pública
  getApiKeyStatus: async () => {
    try {
      const response = await api.get("/api/apikey");
      return response.data;
    } catch (error) {
      throw (
        error.response?.data || {
          success: false,
          message: "Error obteniendo estado de API Key",
        }
      );
    }
  },

  // Generar o regenerar API Key pública
  generateApiKey: async () => {
    try {
      const response = await api.post("/api/apikey/generate");
      return response.data;
    } catch (error) {
      throw (
        error.response?.data || {
          success: false,
          message: "Error generando API Key",
        }
      );
    }
  },

  // Obtener configuración general de la aplicación
  getAppSettings: async () => {
    try {
      const response = await api.get("/api/settings");
      return response.data;
    } catch (error) {
      throw (
        error.response?.data || {
          success: false,
          message: "Error obteniendo configuración",
        }
      );
    }
  },

  // Actualizar configuración general de la aplicación
  updateAppSettings: async (settings) => {
    try {
      const response = await api.post("/api/settings", settings);
      return response.data;
    } catch (error) {
      throw (
        error.response?.data || {
          success: false,
          message: "Error actualizando configuración",
        }
      );
    }
  },

  // Obtener límite de tamaño de subida (para el formulario de upload)
  getUploadLimit: async () => {
    try {
      const response = await api.get("/api/settings/upload-limit");
      return response.data;
    } catch (error) {
      throw (
        error.response?.data || {
          success: false,
          message: "Error obteniendo límite de subida",
        }
      );
    }
  },
};

